import { memo, useCallback, useEffect, useState } from 'react';

import { Box, Chip, IconButton, Paper, Typography, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import {
  ApiType,
  clearOverride,
  getActiveOverrides,
  getApiLabel,
  hasActiveOverrides,
  isOverrideAllowed,
} from '../../utils/api-url-override';

const useStyles = makeStyles(theme => ({
  container: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 9999,
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.warning.light,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[4],
    maxWidth: 300,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  title: {
    fontWeight: 600,
    color: theme.palette.warning.dark,
    fontSize: '0.875rem',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  chip: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    '& .MuiChip-deleteIcon': {
      color: theme.palette.warning.contrastText,
    },
  },
  closeButton: {
    padding: 4,
    marginLeft: theme.spacing(1),
  },
}));

/**
 * Visual indicator component that shows when API URL overrides are active.
 * Displays in the bottom-right corner with the ability to clear individual overrides.
 * Only renders in development/testing environments when overrides are active.
 */
export const ApiOverrideIndicator = memo(() => {
  const styles = useStyles();
  const [overrides, setOverrides] = useState<Record<ApiType, number | null>>({
    hw_api: null,
    php_api: null,
  });
  const [isVisible, setIsVisible] = useState(false);

  const refreshOverrides = useCallback(() => {
    if (!isOverrideAllowed()) {
      setIsVisible(false);
      return;
    }

    const active = getActiveOverrides();
    setOverrides(active);
    setIsVisible(hasActiveOverrides());
  }, []);

  useEffect(() => {
    refreshOverrides();

    // Listen for storage changes (in case overrides are cleared in another tab)
    const handleStorageChange = () => {
      refreshOverrides();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [refreshOverrides]);

  const handleClearOverride = useCallback(
    (apiType: ApiType) => {
      clearOverride(apiType);
      refreshOverrides();
    },
    [refreshOverrides],
  );

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
  }, []);

  if (!isVisible) {
    return null;
  }

  const activeOverrides = (Object.entries(overrides) as [ApiType, number | null][]).filter(
    ([, value]) => value !== null,
  );

  if (activeOverrides.length === 0) {
    return null;
  }

  return (
    <Paper className={styles.container} elevation={4}>
      <Box className={styles.header}>
        <Typography className={styles.title}>PR API Overrides Active</Typography>
        <IconButton className={styles.closeButton} size="small" onClick={handleDismiss} aria-label="Dismiss">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box className={styles.chips}>
        {activeOverrides.map(([apiType, prNumber]) => (
          <Chip
            key={apiType}
            className={styles.chip}
            label={`${getApiLabel(apiType)} PR #${prNumber}`}
            onDelete={() => handleClearOverride(apiType)}
            size="small"
          />
        ))}
      </Box>
    </Paper>
  );
});

ApiOverrideIndicator.displayName = 'ApiOverrideIndicator';
